import type { DriveStep } from "driver.js";
import { ONBOARDING_IDS } from "../constants";

export function getNavigationSteps(isMobile: boolean): DriveStep[] {
    return [
        {
            element: `#${ONBOARDING_IDS.NAV_TEAM_SELECTOR}`,
            popover: {
                title: "Team Selector",
                description:
                    "This dropdown controls which team the entire application is scoped to. " +
                    "Switching here updates all dashboards, player lists, statistics, and analytics to show data for the selected team. " +
                    "You can pick any team currently active in your tournament, or click 'See more' to browse the full Teams section.",
                side: "bottom",
                align: "center",
            },
        },
        {
            element: `#${isMobile ? ONBOARDING_IDS.NAV_DASHBOARD_MOBILE : ONBOARDING_IDS.NAV_DASHBOARD}`,
            popover: {
                title: "Dashboard",
                description:
                    "The Dashboard is the place where you get a complete assessment of performance for any single game or series of matches. " +
                    "From here, you can see players ranked by their KPI, view the achievements they've earned, analyze on-ice shot maps, " +
                    "and compare players against each other or the opposition.",
                side: isMobile ? "top" : "right",
                align: "start",
            },
        },
        {
            element: `#${isMobile ? ONBOARDING_IDS.NAV_PLAYERS_MOBILE : ONBOARDING_IDS.NAV_PLAYERS}`,
            popover: {
                title: "Player List",
                description:
                    "The Player List is the place where you explore the Vålerenga squad for any specific tournament. " +
                    "From here, you can click into individual profiles for a deep dive to see how players perform in specific roles, " +
                    "break down the exact components contributing to their overall KPI, view performance trends over time, " +
                    "and track their personal achievements.",
                side: isMobile ? "top" : "right",
                align: "start",
            },
        },
        {
            element: `#${isMobile ? ONBOARDING_IDS.NAV_TOURNAMENTS_MOBILE : ONBOARDING_IDS.NAV_TOURNAMENTS}`,
            popover: {
                title: "Tournament Analysis",
                description:
                    "The Tournament Analysis tab is the place where you evaluate the team's macro-level performance throughout a specific tournament. " +
                    "From here, you can track our win rate and total KPIs, view current league standings, and follow match-by-match trends. " +
                    "You can also dive into detailed team statistics to see individual player contributions, check unlocked achievements, " +
                    "and run comparisons between top players or rival teams.",
                side: isMobile ? "top" : "right",
                align: "start",
            },
        },
        {
            element: `#${isMobile ? ONBOARDING_IDS.NAV_LEADERBOARDS_MOBILE : ONBOARDING_IDS.NAV_LEADERBOARDS}`,
            popover: {
                title: "Leaderboards",
                description:
                    "The Leaderboards tab is the place where you track and celebrate top performances across different timeframes. " +
                    "From here, you can view player rankings for individual matches, entire tournaments, or full seasons. " +
                    "You can compare the squad based on their Total KPI, Average KPI, goals, assists, or blocked shots.",
                side: isMobile ? "top" : "right",
                align: "start",
            },
        },
        {
            element: `#${isMobile ? ONBOARDING_IDS.NAV_SCOUTING_MOBILE : ONBOARDING_IDS.NAV_SCOUTING}`,
            popover: {
                title: "Scouting",
                description:
                    "The Scouting tab is the place where you evaluate talent across the entire league — and soon, beyond. " +
                    "From here, you can discover potential prospects, filter the comprehensive player database by specific roles or teams, " +
                    "and dive into individual profiles to analyze their KPIs and performance metrics just like you would for our own roster.",
                side: isMobile ? "left" : "bottom",
                align: isMobile ? "center" : "start",
            },
        },
        {
            element: `#${isMobile ? ONBOARDING_IDS.NAV_KPI_MOBILE : ONBOARDING_IDS.NAV_KPI}`,
            popover: {
                title: "KPI Methodology",
                description:
                    "The KPI Methodology tab is the place where you learn exactly how our performance scores are built. " +
                    "From here, you can understand the overall score distribution, break down how specific on-ice actions translate into points, " +
                    "and easily compare our current team setup against the default baseline proposed by the system creators.",
                side: isMobile ? "left" : "bottom",
                align: isMobile ? "center" : "start",
            },
        },
        {
            element: `#${isMobile ? ONBOARDING_IDS.NAV_CHAT_MOBILE : ONBOARDING_IDS.NAV_CHAT}`,
            popover: {
                title: "Våll-e Chat",
                description:
                    "The Våll-e Chat tab is the place where you interact with your AI-powered hockey analytics assistant. " +
                    "From here, you can start a new conversation to analyze player performance, team strategies, and game statistics. " +
                    "During the chat, you can tag specific players, tournaments, or matches, or simply go with the flow and ask anything hockey-related.",
                side: isMobile ? "left" : "bottom",
                align: isMobile ? "center" : "end",
            },
        },
        {
            element: `#${isMobile ? ONBOARDING_IDS.NAV_SETTINGS_MOBILE : ONBOARDING_IDS.NAV_SETTINGS}`,
            popover: {
                title: "Settings",
                description:
                    "The Settings tab is the place where you customize the engine behind the platform. " +
                    "From here, you can adjust exactly how the KPI system evaluates players by modifying the weights and multipliers " +
                    "for specific on-ice statistics. You can also configure your report settings to manage which email addresses " +
                    "receive automated updates for ongoing matches.",
                side: isMobile ? "left" : "bottom",
                align: isMobile ? "center" : "end",
            },
        },
        isMobile
            ? {
                  element: `#${ONBOARDING_IDS.NAV_FOOTER_MOBILE}`,
                  popover: {
                      title: "Footer Links",
                      description:
                          "These three links are also accessible from this menu: " +
                          "Feedback to send us your thoughts and suggestions directly, " +
                          "Privacy Policy to review how your data is handled, " +
                          "and System Status to check whether all platform services are running normally.",
                      side: "left",
                      align: "center",
                  },
              }
            : {
                  element: `#${ONBOARDING_IDS.FOOTER_NAV}`,
                  popover: {
                      title: "Footer",
                      description:
                          "At the bottom of every page you'll find three quick links: " +
                          "Feedback to send us your thoughts and suggestions directly, " +
                          "Privacy Policy to review how your data is handled, " +
                          "and System Status to check whether all platform services are running normally.",
                      side: "top",
                      align: "center",
                  },
              },
    ];
}

export const navigationSteps: DriveStep[] = getNavigationSteps(false);
